import type { ValueOf } from '@common/utils/utilityTypes';
import type { IOrganizationGeneralSettings } from '@common/validation/organization';
import type { IOrganization } from '@frontend/components/interfaces';
import type { OrganizationRelationship } from '@frontend/components/interfaces/organizationRelationship';
import { never } from '@common/utils/never';
import { emptyUuid } from '@common/utils/uuid';

export type OrganizationRelationshipState<T extends IOrganization | IOrganizationGeneralSettings> = {
    organization?: T;
};

export const OrganizationRelationshipsActions = {
    addRelationship: 'addRelationship',
    removeRelationship: 'removeRelationship',
    setOrganization: 'setOrganization',
    updateOrganizationKeyValue: 'updateOrganizationKeyValue',
    updateRelationship: 'updateRelationship',
} as const;

export type OrganizationRelationshipAction<T extends IOrganization | IOrganizationGeneralSettings> =
    | {
          payload: T;
          type: 'setOrganization';
      }
    | {
          payload: {
              key: keyof T;
              value: ValueOf<T>;
          };
          type: 'updateOrganizationKeyValue';
      }
    | {
          payload: OrganizationRelationship;
          type: 'addRelationship';
      }
    | {
          payload: { index: number; relationship: OrganizationRelationship };
          type: 'updateRelationship';
      }
    | {
          payload: number;
          type: 'removeRelationship';
      };

export function organizationRelationshipsReducer<T extends IOrganization | IOrganizationGeneralSettings>(
    state: OrganizationRelationshipState<T>,
    action: OrganizationRelationshipAction<T>
) {
    const { type, payload } = action;
    switch (type) {
        case OrganizationRelationshipsActions.setOrganization:
            return { ...state, organization: payload };

        case OrganizationRelationshipsActions.updateOrganizationKeyValue:
            if (!state.organization) {
                return state;
            }

            return {
                ...state,
                organization: {
                    ...state.organization,
                    [action.payload.key]: payload.value,
                },
            };

        case OrganizationRelationshipsActions.addRelationship:
            if (!state.organization) {
                return state;
            }

            return {
                ...state,
                organization: {
                    ...state.organization,
                    organizationRelationships: [...state.organization.organizationRelationships, payload],
                },
            };

        case OrganizationRelationshipsActions.updateRelationship: {
            if (!state.organization) {
                return state;
            }

            const { index, relationship } = payload;
            const updatedRelationships = [...state.organization.organizationRelationships];

            updatedRelationships[index] = { ...relationship };

            return {
                ...state,
                organization: {
                    ...state.organization,
                    organizationRelationships: updatedRelationships,
                },
            };
        }

        case OrganizationRelationshipsActions.removeRelationship: {
            if (!state.organization) {
                return state;
            }

            const indexToRemove = payload;
            const updatedRelationships = [...state.organization.organizationRelationships];
            if (updatedRelationships[indexToRemove].relatedOrganizationId === emptyUuid) {
                updatedRelationships.splice(indexToRemove, 1);
            } else {
                updatedRelationships[indexToRemove].isDeleted = true;
            }

            return {
                ...state,
                organization: {
                    ...state.organization,
                    organizationRelationships: updatedRelationships,
                },
            };
        }

        default:
            never(type);
    }
}
